import { Injectable } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { saveAs } from 'file-saver';
import { AppHttpService } from './app-http.service';

export interface SpatialSummaryPayload {
  polygon: { type: 'Polygon'; coordinates: number[][][] };
  startDate: string;
  endDate: string;
  statuses: string[];
  /** Set when the map is drilled into one report's bid item: scope the summary to it. */
  reportId?: string;
  bidItemId?: string;
}

export interface SpatialSummaryBidItem {
  bid_item_id: string;
  item: string;
  description: string;
  uom: string;
  unit_price: number | null;
  quantity_in_area: number;
  total_value: number;
  contract_quantity: number | null;
  pct_complete: number | null;
}

export interface SpatialSummaryResponse {
  area_acres: number;
  counts: { reports: number; photos: number; sites: number };
  bid_items: SpatialSummaryBidItem[];
  total_value: number;
}

export interface SpatialSummaryState {
  active: boolean;
  loading: boolean;
  error: string | null;
  summary: SpatialSummaryResponse | null;
  dateLabel: string;
  /** A PDF export is in flight (screenshot + API round-trip). */
  exporting: boolean;
  exportError: string | null;
}

const INITIAL_STATE: SpatialSummaryState = {
  active: false,
  loading: false,
  error: null,
  summary: null,
  dateLabel: '',
  exporting: false,
  exportError: null,
};

const PDF_FALLBACK_FILENAME = 'area-summary.pdf';

@Injectable({ providedIn: 'root' })
export class ProjectSpatialSummaryService {
  /**
   * Shared summary state. Map components (sites-location-map → service)
   * push results here; consumers (map-sidenav) subscribe. Keeps the
   * prop-drilling out of map-layer-container and lets the sidenav own
   * the rendering without knowing about the sketch widget.
   */
  readonly state$ = new BehaviorSubject<SpatialSummaryState>(INITIAL_STATE);

  /**
   * Fired by `requestExport()` (the sidenav's download button). The map
   * component listens, captures a screenshot of the live view, and hands
   * it back through `exportPdf()`. Routing the request through here keeps
   * the sidenav ignorant of the MapView.
   */
  readonly exportRequest$ = new Subject<void>();

  /** Swappable for tests — file-saver has no injectable seam. */
  saveFile: (blob: Blob, fileName: string) => void = saveAs;

  /** The most recent summary request, replayed (plus the map image) for the PDF. */
  private lastRequest: { projectId: string; payload: SpatialSummaryPayload } | null = null;

  constructor(private http: AppHttpService) {}

  setActive(active: boolean, dateLabel: string): void {
    this.lastRequest = null;
    if (active) {
      this.state$.next({ ...INITIAL_STATE, active: true, dateLabel });
    } else {
      this.state$.next({ ...INITIAL_STATE });
    }
  }

  setLoading(): void {
    const prev = this.state$.value;
    this.state$.next({ ...prev, loading: true, error: null });
  }

  setResult(summary: SpatialSummaryResponse): void {
    const prev = this.state$.value;
    this.state$.next({ ...prev, loading: false, error: null, summary });
  }

  setError(error: string): void {
    const prev = this.state$.value;
    this.state$.next({ ...prev, loading: false, error, summary: null });
  }

  fetch(projectId: string, payload: SpatialSummaryPayload): Observable<SpatialSummaryResponse> {
    this.lastRequest = { projectId, payload };
    return this.http.postService(`/v1/project/${projectId}/spatial-summary`, payload);
  }

  /**
   * Kick off a PDF export of the current summary. No-op unless a summary is
   * loaded and nothing is already exporting.
   */
  requestExport(): void {
    const s = this.state$.value;
    if (!s.active || !s.summary || s.loading || s.exporting || !this.lastRequest) {
      return;
    }
    this.state$.next({ ...s, exporting: true, exportError: null });
    this.exportRequest$.next();
  }

  /**
   * Second half of the export: called by the map component with the
   * screenshot (or null when capture failed — the PDF still renders, just
   * without the map). Downloads the file and settles the exporting state.
   */
  exportPdf(mapImage: string | null): void {
    const req = this.lastRequest;
    const s = this.state$.value;
    if (!req || !s.active) {
      this.state$.next({ ...s, exporting: false });
      return;
    }
    const body: SpatialSummaryPayload & { mapImage?: string } = { ...req.payload };
    if (mapImage) {
      body.mapImage = mapImage;
    }
    this.http.postService(
      `/v1/project/${req.projectId}/spatial-summary/pdf`,
      body,
      {},
      { responseType: 'blob', observe: 'response' },
    ).subscribe({
      next: (response: HttpResponse<Blob>) => {
        this.saveFile(response.body, this.resolvePdfFileName(response.headers?.get('Content-Disposition')));
        this.state$.next({ ...this.state$.value, exporting: false, exportError: null });
      },
      error: () => {
        this.state$.next({ ...this.state$.value, exporting: false, exportError: 'Failed to download PDF' });
      },
    });
  }

  private resolvePdfFileName(contentDisposition: string | null): string {
    const match = contentDisposition?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    const name = match?.[1]?.replace(/['"]/g, '').trim();
    return name || PDF_FALLBACK_FILENAME;
  }
}
